import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PronunciationVaultService } from "@/services/agent/PronunciationVaultService";
import { PronunciationChange, PronunciationVaultInput } from "@/types/agent/pronunciation-vault";

const QUERY_KEY = ["pronunciation-vaults"];

export const usePronunciationVaultList = () =>
  useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => PronunciationVaultService.list(),
    staleTime: 5 * 60 * 1000,
  });

export const useCreatePronunciationVault = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PronunciationVaultInput) => PronunciationVaultService.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useUpdatePronunciationVault = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<PronunciationVaultInput> }) =>
      PronunciationVaultService.update(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useDefaultPronunciationsPreview = (enabled: boolean) =>
  useQuery({
    queryKey: [...QUERY_KEY, "defaults-preview"],
    queryFn: () => PronunciationVaultService.previewDefaults(),
    enabled,
    staleTime: 0,
  });

export const useApplyDefaultPronunciations = () =>
  useMutation({
    mutationFn: () => PronunciationVaultService.applyDefaults(),
  });

export const useUpdateDefaultPronunciations = () =>
  useMutation({
    mutationFn: (changes: PronunciationChange[]) => PronunciationVaultService.updateDefaults(changes),
  });

export const useDeletePronunciationVault = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => PronunciationVaultService.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};
